import { ButtonProps } from 'antd';
import { ReactElement, ReactNode, cloneElement } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import { StyledIconWrapper, StyledModal, StyledModalContent, StyledModalFooter, StyledModalFooterRow } from './styles';

type Props = {
    open?: boolean;
    title?: string | ReactElement;
    width?: number;
    type?: 'error' | 'success' | 'warning' | 'default';
    content?: ReactNode;
    confirmButtonText?: string;
    cancelButtonText?: string;
    icon?: ReactElement;
    confirmationButtonDisabled?: boolean;
    confirmationButtonLoading?: boolean;
    confirmButtonProps?: ButtonProps;
    destroyOnClose?: boolean;
    maskClosable?: boolean;
    onConfirm?: (data?: any) => any;
    onCancel?: () => void;
    onClose?: () => void;
    // Replaces the Cancel button with custom content
    footerLeft?: ReactNode;
};

const ConfirmationModal = ({
    title,
    icon,
    open = false,
    type = 'default',
    width = 656,
    content = <p>Default confirmation message</p>,
    confirmButtonText = 'Confirm',
    cancelButtonText = 'Cancel',
    confirmationButtonDisabled = false,
    confirmationButtonLoading = false,
    confirmButtonProps,
    destroyOnClose,
    maskClosable,
    onCancel,
    onConfirm,
    onClose,
    footerLeft,
}: Props) => {
    const iconFontSize = 42;
    const defaultIcon = (
        <StyledIconWrapper $type={type}>
            <InfoCircleOutlined
                style={{
                    fontSize: iconFontSize,
                    color: 'currentColor',
                }}
            />
        </StyledIconWrapper>
    );

    const buttonType = type === 'default' || type === 'warning' ? 'primary' : type;

    const confirmButton = (
        <Button
            {...confirmButtonProps}
            onClick={onConfirm}
            type={buttonType}
            disabled={confirmationButtonDisabled}
            loading={confirmationButtonLoading}
        >
            {confirmButtonText}
        </Button>
    );

    return (
        <StyledModal
            maskClosable={maskClosable}
            destroyOnClose={destroyOnClose}
            width={width}
            footer={
                <StyledModalFooter>
                    {footerLeft ? (
                        <StyledModalFooterRow>
                            {footerLeft}
                            {confirmButton}
                        </StyledModalFooterRow>
                    ) : (
                        <>
                            <Button onClick={onCancel}>{cancelButtonText}</Button>
                            {confirmButton}
                        </>
                    )}
                </StyledModalFooter>
            }
            title={title}
            open={open}
            onCancel={onClose || onCancel}
        >
            <StyledModalContent>
                {icon ? (
                    <StyledIconWrapper $type={type}>
                        {cloneElement(icon, {
                            style: { fontSize: iconFontSize, ...icon.props?.style },
                        })}
                    </StyledIconWrapper>
                ) : (
                    defaultIcon
                )}
                {content}
            </StyledModalContent>
        </StyledModal>
    );
};

export default ConfirmationModal;
